import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTheme, ThemeProvider } from 'styled-components';
import { Button } from '@pearsontechnology/aero-components';
import themes from 'containers/App/themes';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import messages from '../messages';
import landmarkModalMessages from './messages';
import {
  MainList,
  ListItem,
  AugmentedModalActions,
} from './styles';
import { getAllLessons } from '../../../utils';
import { internalConstants } from './constants.js';

const { BACK, FORWARD, BRIEF, BACKMATTER, START, ENTRY } = internalConstants;

/**
 * @description These are the 3 landmark selection modal subtitles
 */
const landmarkModalSubtitle = {
  brief: landmarkModalMessages.brief.defaultMessage,
  start: landmarkModalMessages.start.defaultMessage,
  backmatter: landmarkModalMessages.backmatter.defaultMessage,
};

/**
 * @description Landmark Modals,
 * gathers landmark data necessary for a channel partner export to complete.
 */

const LandmarkModals = ({
  publicationID,
  setShowLandmarkModals,
  exportConfirmState,
  setExportConfirmState,
  modalTitles,
  setAreLandmarksValid,
  actionType }) => {

  const { landmarks } = exportConfirmState.metadata;

  const [selectedItem, setSelectedItem] = useState(landmarks[landmarks.locations[landmarks.index]]);
  const [publicationData, setPublicationData] = useState({ data: null });
  const [location, setLocation] = useState(landmarks.locations[landmarks.index]);

  const listElement = useRef(null);
  const currentLandmarkModalSubtitle = landmarkModalSubtitle[landmarks.locations[landmarks.index]];


  useEffect(() => {
    async function fetchData() {
      const lessons = await getAllLessons(publicationID);
      setPublicationData({
        data: lessons,
      });
    }
    fetchData();
  }, [publicationID]);

  useEffect(() => {
    const previouslySelectedItem = landmarks[landmarks.locations[landmarks.index]];
    if (previouslySelectedItem) {
      setSelectedItem(previouslySelectedItem);
    }
  }, [landmarks]);


  /**
 * @description Validate Landmarks, so we can enable publish button
 * if all selections are present.
 *  @param {string} type
 */
  const validateLandmarks = () => {
    if (landmarks[BRIEF] && landmarks[START] && landmarks[BACKMATTER]) {
      setAreLandmarksValid(true);
    }
  };


  /**
 * @description Checks if user already made selection, if so use that
 *  @param {number} value
 *  @param {string} type
 */
  const addDataToObject = (value) => {
    const { brief, start, backmatter } = landmarks;
    const currentModalIndex = landmarks.index;
    let userSelection = Object.assign(landmarks, {
      [location]: selectedItem,
      index: currentModalIndex + value,
    });

    const exitLandmarkModals = () => {
      // save the landmarks as they are
      setExportConfirmState({ type: actionType.METADATA, value: { landmarks: { ...userSelection } } });
      // set modal sequence back to initial state
      setExportConfirmState({ type: actionType.SET_MODAL_TITLE, value: modalTitles[ENTRY] });
      setShowLandmarkModals(false);
      validateLandmarks();
    };

    //You back out of the modals and exit
    if (userSelection.index <= 0) {
      userSelection = Object.assign(landmarks, {
        brief: selectedItem || brief,
        start,
        backmatter,
        index: 0,
      });
      exitLandmarkModals();
      //You're on the last modal, exit landmark modals
    } else if (userSelection.index >= landmarks.locations.length) {
      userSelection = Object.assign(landmarks, {
        brief,
        start,
        backmatter: selectedItem || backmatter,
        index: 0,
      });
      exitLandmarkModals();
      //save current and go to the next
    } else {
      setExportConfirmState({ type: actionType.METADATA, value: { landmarks: { ...userSelection } } });
    }
  };

  /**
 * @description advanceModals
 * sets the MODAL TITLE, sets location, clears the selected item
 *  @param {number} value
 */
  const handleModalSelection = (value) => {
    setExportConfirmState({ type: actionType.SET_MODAL_TITLE, value: modalTitles[landmarks.locations[landmarks.index + value]] });
    setLocation(landmarks.locations[landmarks.index + value]);
    setSelectedItem("");
  };

  /**
 * @description Handle user clicking on next and back buttons
 *  @param {object} event
 *  @param {string} flag
 */
  const handleButtonClick = (event, backOrForward) => {
    event.stopPropagation();

    switch (backOrForward) {
      case BACK:
        handleModalSelection(-1);
        addDataToObject(-1);
        break;
      case FORWARD:
        handleModalSelection(1);
        addDataToObject(1);
        break;
      default:
        return;
    }
  };

  /**
 * @description toggles active class on user selection
 */
  const handleDataSelection = (event) => {
    const { target } = event;
    const { classList } = target;
    event.preventDefault();
    event.stopPropagation();
    if (classList.contains('active')) {
      setSelectedItem(null);
    } else {
      const activityId = target.attributes[0].value;
      const activityObj = {
        title: target.textContent,
        id: activityId,
      };
      setSelectedItem(activityObj);
    }
  };

  return ((
    <>
      {publicationData?.data !== null ?
        (
          <ThemeProvider theme={themes.light}>
            <p>{currentLandmarkModalSubtitle}</p>
            <MainList onClick={handleDataSelection} ref={listElement}>
              {publicationData.data && publicationData.data.map(({ activityId, title }) => (
                <li
                  activityid={activityId}
                  key={activityId}
                  className={selectedItem?.id === activityId ? "active" : ""}>
                  {title}
                </li>)
              )}
            </MainList>

            <AugmentedModalActions >
              <Button primary onClick={(event) => handleButtonClick(event, BACK)}>
                {intl.formatMessage(messages.backButton)}
              </Button>
              <Button primary onClick={(event) => handleButtonClick(event, FORWARD)}>
                {intl.formatMessage(landmarks.index === landmarks.locations.length - 1 ? landmarkModalMessages.done : messages.nextButton)}
              </Button>
            </AugmentedModalActions>
          </ThemeProvider>
        )
        :
        <ListItem>Loading...</ListItem>
      }
    </>)
  );
};

LandmarkModals.propTypes = {
  publicationID: PropTypes.string,
  setShowLandmarkModals: PropTypes.func,
  exportConfirmState: PropTypes.object,
  setExportConfirmState: PropTypes.func,
  modalTitles: PropTypes.object,
  setAreLandmarksValid: PropTypes.func,
  actionType: PropTypes.object,
};

export default withTheme(LandmarkModals);
